// Models
import { IInvoiceFiles, ProcurementHistoricalDetailsResponse } from "@/main/models/response";

// Utils
import { InvoiceHistoricalActionEnum } from "@/main/services/enum/InvoiceHistoricalActionEnum";
import { getTranslateByKey } from "@/main/services/utils";
import { downloadFile } from "@/main/services/utilsFile";

// Api's
import { invoiceApi } from "@/main/api";

// Components
import QuoteProcurementPartInfo from "@/main/components/shared/quoteProcurementPartInfo/quoteProcurementPartInfo";

// Styles
import * as S from "./styles";

const FileMount = ({ guidInvoiceFile, fileName, idInvoiceHistoricalAction, fileExtension }: IInvoiceFiles) => {
	const nameFile = `${fileName}.${fileExtension.toLowerCase()}`;

	function handleDownloadFile(guidInvoiceFile: string) {
		invoiceApi.getfilebyguidinvoicefile(guidInvoiceFile).then((response) => {
			const base64File = response.data.result;
			const nameFileResponse = `${base64File.fileName}.${base64File.fileExtension.toLowerCase()}`;
			downloadFile(base64File.file, nameFileResponse);
		});
	}

	let labelFile: string | null;
	switch (Number(idInvoiceHistoricalAction)) {
		case InvoiceHistoricalActionEnum.EditFileAdded:
			labelFile = getTranslateByKey("invoiceHistoricalAction.createAddedFile");
			break;
		case InvoiceHistoricalActionEnum.EditFileRemoved:
			labelFile = getTranslateByKey("invoiceHistoricalAction.editRemovedFile");
			break;

		default:
			labelFile = null;
			break;
	}
	return (
		<span>
			{labelFile && labelFile != "" && <strong>{labelFile}: </strong>}
			<S.FileLink onClick={() => handleDownloadFile(guidInvoiceFile)}>{nameFile}</S.FileLink>
		</span>
	);
};
interface IDetailsSupplierPartsProps extends ProcurementHistoricalDetailsResponse {
	showPrices: boolean;
}
function DetailsSupplierParts({
	deliveryDays,
	quantity,
	statusCurrent,
	statusPrevious,
	supplierName,
	totalPrice,
	reason,
	reasonDescription,
	invoiceNumber,
	issueDate,
	invoiceFiles,
	fieldsChanged,
	quoteProcurementPartInfo,
	showPrices,
	previousPrice,
	previousDeliveryDeadline,
	paymentVoucherId,
	shippingCompany,
	trackingCode,
}: IDetailsSupplierPartsProps) {
	const partsTranslate = getTranslateByKey("information.parts").toLowerCase();
	const totalPriceTranslate = getTranslateByKey("information.totalPrice");
	const deliveryDaysTranslate = getTranslateByKey("information.deliveryDays").toLowerCase();
	const originalPartsTranslate = getTranslateByKey("information.originalPrice").toLowerCase();
	const originalTermTranslate = getTranslateByKey("information.originalterm").toLowerCase();

	function renderShippingTracking(shippingCompany?: string, trackingCode?: string): string {
		if (shippingCompany && trackingCode) {
			return `${shippingCompany} - ${trackingCode}`;
		} else if (shippingCompany) {
			return shippingCompany;
		} else if (trackingCode) {
			return trackingCode;
		} else {
			return "";
		}
	}

	return (
		<S.Container>
			{(statusPrevious || statusCurrent) && (
				<S.TitleStatusChanged>
					{statusPrevious}
					&nbsp;&rarr;&nbsp;
					{statusCurrent}
				</S.TitleStatusChanged>
			)}
			{/* Supplier info */}
			<S.TitleSupplierName>
				<span>{supplierName}</span>
			</S.TitleSupplierName>

			<p>{renderShippingTracking(shippingCompany, trackingCode)}</p>

			{/* Part information */}
			<QuoteProcurementPartInfo
				data={quoteProcurementPartInfo}
				renderType="column"
				showLineSupplierInfo={{ info: "partType" }}
			/>

			<div style={{ display: "flex", flexDirection: "column" }}>
				{quantity && <p style={{ margin: 0 }}>{`${quantity} ${partsTranslate}`}</p>}

				{showPrices && totalPrice && (
					<p style={{ margin: 0 }}>
						{`${totalPriceTranslate}: ${totalPrice}`}
						{previousPrice && ` (${originalPartsTranslate}: ${previousPrice})`}
					</p>
				)}

				{(deliveryDays || deliveryDays === 0) && (
					<p style={{ margin: 0 }}>
						{`${deliveryDays} ${deliveryDaysTranslate}`}
						{previousDeliveryDeadline && ` (${originalTermTranslate}: ${previousDeliveryDeadline})`}
					</p>
				)}
			</div>
			{paymentVoucherId && (
				<p>
					<strong>{getTranslateByKey("information.Voucher")}: </strong>
					<span>{paymentVoucherId}</span>
				</p>
			)}
			{invoiceNumber && (
				<p>
					<span>{getTranslateByKey("information.invoiceNumber")}: </span>
					<strong>{invoiceNumber}</strong>
				</p>
			)}
			{issueDate && (
				<p>
					<span>{getTranslateByKey("information.issueDate")}: </span>
					<strong>{issueDate}</strong>
				</p>
			)}
			{reason && (
				<p>
					<span>
						<strong>{getTranslateByKey("information.reason")}: </strong>
					</span>
					{reason} {reasonDescription && `(${reasonDescription})`}
				</p>
			)}

			{fieldsChanged && fieldsChanged.length > 0 && (
				<S.BoxFieldsChanged>
					{fieldsChanged.map((item, index) => (
						<div key={index}>
							{item.field && (
								<p>
									<span>
										<strong>{`${getTranslateByKey("SupplierInventoryHistory.field")}: `}</strong>
									</span>
									{item.field}
								</p>
							)}
							{item.from && (
								<p>
									<span>
										<strong>{`${getTranslateByKey("information.from")}: `}</strong>
									</span>
									{item.from}
								</p>
							)}
							{item.to && (
								<p>
									<span>
										<strong>{`${getTranslateByKey("information.to")}: `}</strong>
									</span>
									{item.to}
								</p>
							)}
						</div>
					))}
				</S.BoxFieldsChanged>
			)}

			{invoiceFiles && invoiceFiles.length > 0 && (
				<S.ListFiles
					margin={
						!!invoiceFiles.find((item) => item.idInvoiceHistoricalAction != InvoiceHistoricalActionEnum.CreateFileAdded)
					}
				>
					{invoiceFiles.map((invoice) => {
						return (
							<FileMount
								key={`${invoice.idInvoiceHistoricalAction}-${invoice.fileName}`}
								fileName={invoice.fileName}
								idInvoiceHistoricalAction={invoice.idInvoiceHistoricalAction}
								guidInvoiceFile={invoice.guidInvoiceFile}
								fileExtension={invoice.fileExtension}
							/>
						);
					})}
				</S.ListFiles>
			)}
		</S.Container>
	);
}

export default DetailsSupplierParts;
